import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { Shield } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { authAPI } from '@/utils/api';
import { toast } from 'sonner';
import { trackAccountCreated } from '@/utils/analytics';
import Header from '@/components/Header';

const RegisterPage = () => {
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    nom: '',
    email: '',
    telephone: '',
    password: '',
    confirmPassword: ''
  });
  const [loading, setLoading] = useState(false);
  const [dossierId, setDossierId] = useState(null);
  const [tempToken, setTempToken] = useState(null);

  useEffect(() => {
    // Récupérer les infos du dossier depuis sessionStorage
    const linkDossierId = sessionStorage.getItem('link_dossier_id');
    const linkTempToken = sessionStorage.getItem('link_temp_token');
    const linkEmail = sessionStorage.getItem('link_dossier_email');

    if (linkDossierId && linkTempToken) {
      setDossierId(linkDossierId);
      setTempToken(linkTempToken);
    }
    // Pré-remplir l'email saisi lors du dépôt du dossier (évite de le retaper).
    if (linkEmail) {
      setFormData((prev) => ({ ...prev, email: linkEmail }));
    }
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (formData.password !== formData.confirmPassword) {
      toast.error('Les mots de passe ne correspondent pas');
      return;
    }

    if (formData.password.length < 6) {
      toast.error('Le mot de passe doit contenir au moins 6 caractères');
      return;
    }

    setLoading(true);

    try {
      // Appel avec dossier_id et temp_token si disponibles
      const response = await authAPI.register({
        nom: formData.nom,
        email: formData.email,
        telephone: formData.telephone,
        password: formData.password
      }, dossierId, tempToken);

      localStorage.setItem('token', response.data.access_token);
      localStorage.setItem('user', JSON.stringify(response.data.user));

      // Nettoyer sessionStorage
      sessionStorage.removeItem('link_dossier_id');
      sessionStorage.removeItem('link_temp_token');
      sessionStorage.removeItem('link_dossier_email');

      toast.success('Inscription réussie ! Votre dossier a été lié à votre compte.');

      // Track account creation
      trackAccountCreated('email');

      // Rediriger vers le dossier si lié, sinon dashboard
      if (dossierId) {
        navigate(`/score/${dossierId}`);
      } else {
        navigate('/dashboard');
      }
    } catch (error) {
      console.error('Register error:', error);
      const detail = error.response?.data?.detail;
      const alreadyExists =
        error.response?.status === 400 &&
        typeof detail === 'string' &&
        detail.toLowerCase().includes('already');
      if (alreadyExists) {
        // L'utilisateur a déjà un compte avec cet email : on l'aiguille vers la
        // connexion SANS effacer la liaison dossier ni le flag resume_payment,
        // pour que le paiement reprenne automatiquement après login.
        toast.info('Vous avez déjà un compte avec cet email. Connectez-vous pour continuer.');
        navigate('/login?from=payment');
        return;
      }
      toast.error(detail || 'Erreur lors de l\'inscription');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex flex-col" style={{ backgroundColor: '#f8fafc' }}>
      <Header />
      <div className="flex-1 flex items-center justify-center py-12">
        <div className="w-full max-w-md px-4">
          <div className="card p-8">
          <div className="text-center mb-8">
            <Shield size={48} color="#1e3a8a" className="mx-auto mb-4" strokeWidth={2.5} />
            <h1 className="text-3xl font-bold" style={{ color: '#1e3a8a' }}>
              Inscription
            </h1>
            <p className="text-gray-600 mt-2">Créez votre compte</p>
          </div>

          <form data-testid="register-form" onSubmit={handleSubmit} className="space-y-6">
            <div>
              <Label htmlFor="nom">Nom complet</Label>
              <Input
                id="nom"
                data-testid="input-nom"
                type="text"
                required
                value={formData.nom}
                onChange={(e) => setFormData({ ...formData, nom: e.target.value })}
                placeholder="Votre nom"
                className="mt-2"
              />
            </div>

            <div>
              <Label htmlFor="email">Email</Label>
              <Input
                id="email"
                data-testid="input-email"
                type="email"
                required
                value={formData.email}
                onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                placeholder="votre@email.fr"
                className="mt-2"
              />
            </div>

            <div>
              <Label htmlFor="telephone">Téléphone</Label>
              <Input
                id="telephone"
                data-testid="input-telephone"
                type="tel"
                required
                value={formData.telephone}
                onChange={(e) => setFormData({ ...formData, telephone: e.target.value })}
                placeholder="06 12 34 56 78"
                className="mt-2"
              />
            </div>

            <div>
              <Label htmlFor="password">Mot de passe</Label>
              <Input
                id="password"
                data-testid="input-password"
                type="password"
                required
                value={formData.password}
                onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                placeholder="••••••••"
                className="mt-2"
              />
              <p className="text-sm text-gray-500 mt-1">Minimum 6 caractères</p>
            </div>

            <div>
              <Label htmlFor="confirmPassword">Confirmer le mot de passe</Label>
              <Input
                id="confirmPassword"
                data-testid="input-confirm-password"
                type="password"
                required
                value={formData.confirmPassword}
                onChange={(e) => setFormData({ ...formData, confirmPassword: e.target.value })}
                placeholder="••••••••"
                className="mt-2"
              />
            </div>

            <Button
              data-testid="submit-register-button"
              type="submit"
              size="lg"
              disabled={loading}
              className="w-full"
              style={{ backgroundColor: '#1e3a8a', color: 'white' }}
            >
              {loading ? 'Inscription...' : 'S\'inscrire'}
            </Button>
          </form>

          <div className="mt-6 text-center">
            <p className="text-gray-600">
              Déjà un compte ?{' '}
              <Link
                to="/login"
                data-testid="login-link"
                className="font-semibold"
                style={{ color: '#1e3a8a' }}
              >
                Se connecter
              </Link>
            </p>
          </div>

          <div className="mt-4 text-center">
            <Link
              to="/"
              data-testid="back-home-link"
              className="text-gray-500 hover:text-gray-700"
            >
              Retour à l'accueil
            </Link>
          </div>
        </div>
        </div>
      </div>
    </div>
  );
};

export default RegisterPage;